/**
 * Baseline Accounting's charts: IAS accounting's own set, from the workbook's figures.
 *
 * One set of charts serves both accounting disciplines (ChartsGrid). The backend supplies the
 * series keyed as IAS keys its periods (FY_2024, Q1_2024, M01_2024), so the only thing built
 * here is the period context - from those keys themselves, so it is exactly the project's
 * periods, whatever its shape.
 */

import { ChartsGrid } from '../../ChartsGrid';
import type { ForecastContext, QuarterlyData } from '../../../types/financial-model';
import type { MComputed } from '../../../store/m-accounting';

function contextFor(keys: string[], periodType: string): ForecastContext {
  const quartersByYear: Record<number, string[]> = {};
  for (const key of keys) {
    const year = Number(key.slice(-4));
    (quartersByYear[year] ??= []).push(key);
  }
  const fiscalYears = Object.keys(quartersByYear).map(Number).sort((a, b) => a - b);
  return {
    allQuarters: keys,
    fiscalYears,
    quartersByYear,
    numQuarters: keys.length,
    periodsPerYear: periodType === 'monthly' ? 12 : periodType === 'quarterly' ? 4 : 1,
    periodType,
    firstQuarter: keys[0] ?? '',
    lastQuarter: keys[keys.length - 1] ?? '',
  };
}

export function MCharts({ computed }: { computed: MComputed }) {
  const s = computed.chartSeries;
  if (!s) return null;
  const series = (name: string): QuarterlyData => s[name] ?? {};
  const ctx = contextFor(Object.keys(series('totalRevenue')), computed.layout?.periodType ?? 'annual');

  return (
    <ChartsGrid
      accountingOnly
      padded={false}
      data={{
        ctx,
        annualSummary: computed.annualSummary,
        totalRevenue: series('totalRevenue'),
        grossProfit: series('grossProfit'),
        ebitda: series('ebitda'),
        profitAfterTax: series('profitAfterTax'),
        closingCash: series('closingCash'),
        netOperatingCashflow: series('netOperatingCashflow'),
        netInvestingCashflow: series('netInvestingCashflow'),
        netFinancingCashflow: series('netFinancingCashflow'),
        fixedAssetsNet: series('fixedAssetsNet'),
        totalCurrentAssets: series('totalCurrentAssets'),
        totalLiabilitiesAndEquity: series('totalLiabilitiesAndEquity'),
        tradeDebtors: series('tradeDebtors'),
        tradeCreditors: series('tradeCreditors'),
        stocks: series('stocks'),
        totalAssets: series('totalAssets'),
        totalLiabilities: series('totalLiabilities'),
        netAssets: series('netAssets'),
      }}
    />
  );
}
